/**
 * Authentication hook for managing user state.
 * 
 * Provides:
 * - user: Current authenticated user or null
 * - isLoading: True while checking auth status
 * - isAuthenticated: True if user is logged in
 * - isApproved: True if user is approved
 * - isAuthor: True if user has author role
 * - logout: Function to log out
 * - refetch: Function to refresh user data
 * 
 * Uses TanStack Query for caching and automatic refetching.
 * 
 * Called by:
 * - App.tsx for loading state
 * - ProtectedRoute.tsx for auth checks
 * - AppLayout.tsx for user menu
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getCurrentUser, logout as apiLogout } from '../services/api';
import type { User } from '@compendium/shared';

/**
 * Query key for user data.
 * Used by TanStack Query for caching.
 */
const USER_QUERY_KEY = ['user'];

/**
 * Hook return type.
 */
interface UseAuthReturn {
  /** Current user or null if not authenticated */
  user: User | null;
  /** True while fetching user data */
  isLoading: boolean;
  /** True if user is authenticated */
  isAuthenticated: boolean;
  /** True if user is authenticated and approved */
  isApproved: boolean;
  /** True if user account is blocked */
  isBlocked: boolean;
  /** True if user is an approved author */
  isAuthor: boolean;
  /** Log out and clear user data */
  logout: () => Promise<void>;
  /** Refresh user data from server */
  refetch: () => Promise<void>;
}

/**
 * Authentication hook.
 * Fetches and caches current user data.
 * 
 * @returns Auth state and methods
 * 
 * Example:
 * ```tsx
 * const { user, isAuthenticated, logout } = useAuth();
 * if (!isAuthenticated) {
 *   return <Navigate to="/login" />;
 * }
 * ```
 */
export function useAuth(): UseAuthReturn {
  const queryClient = useQueryClient();

  // Fetch current user with TanStack Query
  const { data: user, isLoading, refetch } = useQuery({
    queryKey: USER_QUERY_KEY,
    queryFn: getCurrentUser,
    // Don't retry on 401 (not authenticated)
    retry: false,
    // Refetch on mount to ensure fresh data
    refetchOnMount: true,
    // Cache for 5 minutes
    staleTime: 5 * 60 * 1000,
  });

  /**
   * Log out user and clear cached data.
   */
  async function logout(): Promise<void> {
    await apiLogout();
    // Clear user from cache
    queryClient.setQueryData(USER_QUERY_KEY, null);
    // Invalidate all queries to refetch with new auth state
    await queryClient.invalidateQueries();
  }

  /**
   * Refetch user data from server.
   */
  async function handleRefetch(): Promise<void> {
    await refetch();
  }

  return {
    user: user ?? null,
    isLoading,
    isAuthenticated: !!user,
    isApproved: user?.status === 'approved',
    isBlocked: user?.status === 'blocked',
    isAuthor: user?.status === 'approved' && user?.role === 'author',
    logout,
    refetch: handleRefetch,
  };
}
